import { useState, useEffect, useCallback, useMemo } from "react";
import { useRemittance } from "../remittance/useRemittance";
import { useRemittanceItem } from "../remittanceItem/useRemittanceItem";
import { useDeductionDrawer } from "./useDeductionDrawer";
import { Remittance, RemittanceItem } from "@/core/entities/deduction";

export enum Direction {
  NEXT = "next",
  PREVIOUS = "previous",
}

export function useDeductionNavigation() {
  const { drawerInfo, isDrawerOpen, setDrawerInfo, setIsFirstItem, setIsLastItem } = useDeductionDrawer();
  const {
    remittances,
    currentExpandedRemittanceKey,
    handleExpandRemittance,
    currentPage: currentRemittancePage,
    handlePageChange: setCurrentRemittancePage,
    totalPages: totalRemittancePages,
    isLoading: isRemittancesLoading,
  } = useRemittance();
  const {
    remittanceItems,
    currentPage: currentRemittanceItemPage,
    handlePageChange: setCurrentRemittanceItemPage,
    totalPages: totalRemittanceItemPages,
    isLoading: isRemittanceItemsLoading,
  } = useRemittanceItem();

  const currentPage = useMemo(
    () => (drawerInfo?.isRemittanceItemsPage ? currentRemittanceItemPage : currentRemittancePage),
    [drawerInfo?.isRemittanceItemsPage, currentRemittanceItemPage, currentRemittancePage]
  );

  const setCurrentPage = useMemo(
    () => (drawerInfo?.isRemittanceItemsPage ? setCurrentRemittanceItemPage : setCurrentRemittancePage),
    [drawerInfo?.isRemittanceItemsPage, setCurrentRemittanceItemPage, setCurrentRemittancePage]
  );

  const totalPages = useMemo(
    () => (drawerInfo?.isRemittanceItemsPage ? totalRemittanceItemPages : totalRemittancePages),
    [drawerInfo?.isRemittanceItemsPage, totalRemittanceItemPages, totalRemittancePages]
  );

  const isLoading = useMemo(
    () => (drawerInfo?.isRemittanceItemsPage ? isRemittanceItemsLoading : isRemittancesLoading),
    [drawerInfo?.isRemittanceItemsPage, isRemittanceItemsLoading, isRemittancesLoading]
  );

  const [direction, setDirection] = useState<Direction>(Direction.NEXT);
  const [isPageChanging, setIsPageChanging] = useState(false);

  const findTargetItem = useCallback((items: RemittanceItem[], direction: Direction) => {
    return (direction === Direction.NEXT ? items : [...items].reverse()).find(item => item.isDeduction || item.isCredit);
  }, []);

  const isFirstRemittanceItem = useCallback(() => {
    if (!drawerInfo) return false;

    if (drawerInfo?.isRemittanceItemsPage) {
      const firstDeductionItem = remittanceItems.find(item => item.isDeduction || item.isCredit);
      return currentPage === 1 && drawerInfo.remittanceItemId === firstDeductionItem?.remittanceItemId;
    } else {
      const firstRemittance = remittances[0];
      if (!firstRemittance) return false;

      const firstDeductionItem = firstRemittance.remittanceItems.find(item => item.isDeduction || item.isCredit);
      return currentPage === 1 && drawerInfo.remittanceItemId === firstDeductionItem?.remittanceItemId;
    }
  }, [drawerInfo, remittanceItems, remittances, currentPage]);

  const isLastRemittanceItem = useCallback(() => {
    if (!drawerInfo) return false;

    if (drawerInfo?.isRemittanceItemsPage) {
      const lastDeductionItem = [...remittanceItems].reverse().find(item => item.isDeduction || item.isCredit);
      return currentPage === totalPages && drawerInfo.remittanceItemId === lastDeductionItem?.remittanceItemId;
    } else {
      const lastRemittance = remittances.at(-1);
      if (!lastRemittance) return false;

      const lastDeductionItem = [...lastRemittance.remittanceItems].reverse().find(item => item.isDeduction || item.isCredit);
      return currentPage === totalPages && drawerInfo.remittanceItemId === lastDeductionItem?.remittanceItemId;
    }
  }, [drawerInfo, remittanceItems, remittances, currentPage, totalPages]);

  const scrollTable = useCallback(() => {
    const tableContainer = document.querySelector(".table-container.sticky");
    const currentRemittanceItem = document.querySelector(".current-remittance-item");

    if (currentRemittanceItem && tableContainer) {
      const containerRect = tableContainer?.getBoundingClientRect() ?? { top: 0 };
      const itemRect = currentRemittanceItem.getBoundingClientRect();
      const itemTopRelativeToContainer = itemRect.top - (containerRect?.top ?? 0) + (tableContainer?.scrollTop ?? 0);
      const scrollTop = itemTopRelativeToContainer - (tableContainer?.clientHeight ?? 0) / 2 + (itemRect.height ?? 0) / 2;
      tableContainer?.scrollTo({ top: scrollTop, behavior: "smooth" });
    }
  }, []);

  const getTargetRemittanceItem = useCallback((items: RemittanceItem[], currentItemId: number, direction: Direction) => {
    const currentIndex = items.findIndex(item => item.remittanceItemId === currentItemId);
    if (currentIndex === -1) return null;

    const targetIndex = currentIndex + (direction === Direction.NEXT ? 1 : -1);
    return items[targetIndex]?.isDeduction || items[targetIndex]?.isCredit ? items[targetIndex] : null;
  }, []);

  const updateDrawerInfo = useCallback(
    (targetItem: RemittanceItem, checkNumber: string) => {
      if (!targetItem) return;
      setDrawerInfo({
        remittanceItemId: targetItem.remittanceItemId,
        remittanceItem: targetItem,
        checkNumber,
        invoiceNumber: targetItem.invoiceNumber,
        isRemittanceItemsPage: drawerInfo?.isRemittanceItemsPage ?? false,
        purchaserKey: targetItem.purchaser?.purchaserKey ?? "",
        isDeductionIncomplete: targetItem.isIncompleteDeduction ?? false,
        isManuallyUploaded: targetItem.isManuallyUploaded ?? false,
        nextPrevEnabled: true,
      });
    },
    [drawerInfo?.isRemittanceItemsPage, setDrawerInfo]
  );

  const changePageHandler = useCallback(
    (direction: Direction) => {
      if (currentPage === 1 && direction === Direction.PREVIOUS) return;
      if (currentPage === totalPages && direction === Direction.NEXT) return;
      setIsPageChanging(true);
      setCurrentPage(currentPage + (direction === Direction.NEXT ? 1 : -1));
    },
    [currentPage, totalPages, setCurrentPage]
  );

  const handleRemittanceItemNavigation = useCallback(
    (direction: Direction) => {
      if (!drawerInfo) return;

      const targetItem = getTargetRemittanceItem(remittanceItems, drawerInfo.remittanceItemId, direction);
      if (targetItem) return updateDrawerInfo(targetItem, targetItem?.checkNo ?? "");
      changePageHandler(direction);
    },
    [drawerInfo, remittanceItems, getTargetRemittanceItem, updateDrawerInfo, changePageHandler]
  );

  const handleRemittanceNavigation = useCallback(
    (direction: Direction) => {
      if (!drawerInfo) return;

      const currentRemittanceIndex = remittances.findIndex(rem => rem.checkNumber === drawerInfo.checkNumber);
      if (currentRemittanceIndex === -1) return;

      const remittance = remittances[currentRemittanceIndex];
      const targetItem = getTargetRemittanceItem(remittance.remittanceItems, drawerInfo.remittanceItemId, direction);

      if (targetItem) {
        if (!currentExpandedRemittanceKey || currentExpandedRemittanceKey == null || currentExpandedRemittanceKey !== remittance.remittanceId) {
          handleExpandRemittance(remittance.remittanceId);
        }
        updateDrawerInfo(targetItem, remittance.checkNumber);
      } else {
        const findNextValidRemittance = (startIndex: number): { remittance: Remittance; targetItem: RemittanceItem } | null => {
          const nextIndex = startIndex + (direction === Direction.NEXT ? 1 : -1);
          if (nextIndex < 0 || nextIndex >= remittances.length) {
            return null;
          }

          const nextRemittance = remittances[nextIndex];
          const targetItem = (direction === Direction.NEXT ? nextRemittance.remittanceItems : [...nextRemittance.remittanceItems].reverse()).find(
            item => item.isDeduction || item.isCredit
          );

          if (targetItem) {
            return { remittance: nextRemittance, targetItem };
          }

          return findNextValidRemittance(nextIndex);
        };

        const result = findNextValidRemittance(currentRemittanceIndex);
        if (result) {
          handleExpandRemittance(result.remittance.remittanceId);
          updateDrawerInfo(result.targetItem, result.remittance.checkNumber);
        } else {
          changePageHandler(direction);
        }
      }
    },
    [drawerInfo, remittances, currentExpandedRemittanceKey, handleExpandRemittance, getTargetRemittanceItem, updateDrawerInfo, changePageHandler]
  );

  const handleDeductionNavigation = useCallback(
    (direction: Direction) => {
      if (!isDrawerOpen || !drawerInfo || isLoading) return;
      setDirection(direction);
      if (drawerInfo.isRemittanceItemsPage) {
        handleRemittanceItemNavigation(direction);
      } else {
        handleRemittanceNavigation(direction);
      }
      scrollTable();
    },
    [isDrawerOpen, drawerInfo, isLoading, handleRemittanceItemNavigation, handleRemittanceNavigation, scrollTable]
  );

  const handleRemittanceItemsPageNavigation = useCallback(() => {
    if (remittanceItems.length === 0) return;

    const targetItem = findTargetItem(remittanceItems, direction);
    if (targetItem) {
      updateDrawerInfo(targetItem, targetItem.checkNo as string);
      setIsPageChanging(false);
      scrollTable();
    }
  }, [remittanceItems, direction, findTargetItem, updateDrawerInfo, scrollTable]);

  const handleRemittancesPageNavigation = useCallback(() => {
    if (remittances.length === 0) return;

    const findRemittanceWithDeduction = (startIndex: number): { remittance: Remittance; targetItem: RemittanceItem } | null => {
      if (startIndex < 0 || startIndex >= remittances.length) {
        return null;
      }

      const remittance = remittances[startIndex];
      const targetItem = findTargetItem(remittance.remittanceItems, direction);

      if (targetItem) {
        return { remittance, targetItem };
      }

      // Search in the appropriate direction
      const nextIndex = direction === Direction.NEXT ? startIndex + 1 : startIndex - 1;
      return findRemittanceWithDeduction(nextIndex);
    };

    const startIndex = direction === Direction.NEXT ? 0 : remittances.length - 1;
    const result = findRemittanceWithDeduction(startIndex);

    if (result) {
      handleExpandRemittance(result.remittance.remittanceId);
      updateDrawerInfo(result.targetItem, result.remittance.checkNumber);
      setIsPageChanging(false);
      scrollTable();
    }
  }, [remittances, direction, findTargetItem, handleExpandRemittance, updateDrawerInfo, scrollTable]);

  useEffect(() => {
    if (!isPageChanging || isLoading) return;

    if (drawerInfo?.isRemittanceItemsPage) {
      setTimeout(() => {
        handleRemittanceItemsPageNavigation();
      }, 0);
    } else {
      setTimeout(() => {
        handleRemittancesPageNavigation();
      }, 0);
    }
  }, [drawerInfo?.isRemittanceItemsPage, isPageChanging, isLoading, handleRemittanceItemsPageNavigation, handleRemittancesPageNavigation]);

  useEffect(() => {
    setIsFirstItem(isFirstRemittanceItem() ?? false);
    setIsLastItem(isLastRemittanceItem() ?? false);
  }, [drawerInfo?.remittanceItemId, setIsFirstItem, setIsLastItem, isFirstRemittanceItem, isLastRemittanceItem]);

  return { handleDeductionNavigation };
}
